import {
  combineDateWithTime,
  convertUtcToOrgTimezone,
} from '~/core/generic/utils/timezoneUtils';
import { TZDate } from 'react-day-picker';

export interface TimeOption {
  value: string; // 24-hour format HH:MM
  label: string; // 12-hour format display
}

/**
 * Generate time options in 30-minute intervals
 * @returns Array of time options from 12:00 AM to 11:30 PM
 */
export function generateTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const label = formatTimeTo12Hour(value);
      options.push({ value, label });
    }
  }

  return options;
}

/**
 * Get the current time in organization timezone using TZDate
 * @param timezone Organization timezone
 * @returns Current time in HH:MM format
 */
function getCurrentTimeInTimezone(timezone: string): string {
  const now = new TZDate(new Date(), timezone);
  const hour = now.getHours().toString().padStart(2, '0');
  const minute = now.getMinutes().toString().padStart(2, '0');
  return `${hour}:${minute}`;
}

/**
 * Check if a date string represents today in the organization timezone
 * @param dateString Date string in YYYY-MM-DD format
 * @param timezone Organization timezone
 * @returns True if the date is today
 */
function isToday(dateString: string, timezone: string): boolean {
  const now = new TZDate(new Date(), timezone);
  const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return dateString === todayString;
}

/**
 * Generate time options, filtering out past times if the date is today
 * @param dateString Optional date string to check if it's today
 * @param timezone Organization timezone
 * @returns Array of valid time options
 */
export function generateValidTimeOptions(
  dateString?: string | null,
  timezone?: string,
): TimeOption[] {
  const allOptions = generateTimeOptions();

  if (!dateString || !timezone) {
    return allOptions;
  }

  if (!isToday(dateString, timezone)) {
    return allOptions;
  }

  const currentTime = getCurrentTimeInTimezone(timezone);
  const [currentHour, currentMinute] = currentTime.split(':').map(Number);
  const currentMinutes = currentHour * 60 + currentMinute;

  return allOptions.filter((option) => {
    const [hour, minute] = option.value.split(':').map(Number);
    const optionMinutes = hour * 60 + minute;
    return optionMinutes > currentMinutes;
  });
}

/**
 * Get the next valid time slot (at least 30 minutes from now)
 * @param timezone Organization timezone
 * @returns Next valid time in HH:MM format
 */
function getNextValidTime(timezone: string): string {
  const now = new TZDate(new Date(), timezone);
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Round up to next 30-minute interval
  let nextMinute = currentMinute < 30 ? 30 : 0;
  let nextHour = currentMinute < 30 ? currentHour : currentHour + 1;

  if (nextHour >= 24) {
    nextHour = 23;
    nextMinute = 30;
  }

  return `${nextHour.toString().padStart(2, '0')}:${nextMinute.toString().padStart(2, '0')}`;
}

/**
 * Validate and adjust time if needed for a given date
 * If the date is today and the time is in the past, return the next valid time
 * @param dateString Date string in YYYY-MM-DD format
 * @param timeString Time string in HH:MM format
 * @param timezone Organization timezone
 * @returns Valid time string in HH:MM format
 */
export function getValidTimeForDate(
  dateString: string,
  timeString: string,
  timezone: string,
): string {
  if (!isToday(dateString, timezone)) {
    return timeString;
  }

  const now = new TZDate(new Date(), timezone);
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const [timeHour, timeMinute] = timeString.split(':').map(Number);

  const currentMinutes = currentHour * 60 + currentMinute;
  const timeMinutes = timeHour * 60 + timeMinute;

  if (timeMinutes <= currentMinutes) {
    return getNextValidTime(timezone);
  }

  return timeString;
}

/**
 * Convert 24-hour time to 12-hour format for display
 * @param time24 Time in HH:MM format (24-hour)
 * @returns Time in 12-hour format with AM/PM
 */
export function formatTimeTo12Hour(time24: string): string {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Extract the current time from a UTC ISO string in organization timezone
 * @param utcIsoString UTC ISO string from reservation
 * @param timezone Organization timezone
 * @returns Time in HH:MM format (24-hour)
 */
export function extractTimeFromUtcString(
  utcIsoString: string,
  timezone: string,
): string {
  const orgTimeInfo = convertUtcToOrgTimezone(utcIsoString, timezone);
  return orgTimeInfo.timeOnly;
}

/**
 * Create a new UTC ISO string with updated time while preserving the date
 * @param currentUtcIsoString Current UTC ISO string
 * @param newTime New time in HH:MM format (24-hour)
 * @param timezone Organization timezone
 * @returns New UTC ISO string with updated time
 */
export function createUpdatedUtcString(
  currentUtcIsoString: string,
  newTime: string,
  timezone: string,
): string {
  const orgTimeInfo = convertUtcToOrgTimezone(currentUtcIsoString, timezone);

  const newDateTime = combineDateWithTime(
    orgTimeInfo.dateOnly,
    newTime,
    timezone,
  );

  return newDateTime.utcIsoString;
}
